/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  FloatingFocusManager,
  FloatingPortal,
  type OffsetOptions,
  type Placement,
  useMergeRefs,
} from '@floating-ui/react';
import classNames from 'classnames';
import React from 'react';

import {
  type CommonProps,
  type PolymorphicCommonProps,
} from '../_common/types';
import { ConditionalWrap } from '../conditional-wrap';
import { useIsInsideDialog } from '../dialog/dialog-context';
import { useNeedleTheme } from '../theme/ThemeProvider';
import { usePopover } from './use-popover';
import { usePatchFloatingFocusGuards } from './usePatchFloatingFocusGuards';

type PlacementOptions =
  | 'bottom-end-top-end'
  | 'bottom-start-top-start'
  | 'bottom-middle-top-middle'
  | 'top-end-bottom-end'
  | 'top-start-bottom-start'
  | 'top-middle-bottom-middle'
  | 'bottom-start-bottom-end'
  | 'top-start-top-end'
  | 'middle-start-middle-end'
  | 'bottom-end-bottom-start'
  | 'top-end-top-start'
  | 'middle-end-middle-start';

export const placementTranslation: Record<PlacementOptions, Placement> = {
  'bottom-end-bottom-start': 'right-end',
  'bottom-end-top-end': 'bottom-end',
  'bottom-middle-top-middle': 'bottom',
  'bottom-start-bottom-end': 'left-end',
  'bottom-start-top-start': 'bottom-start',
  'middle-end-middle-start': 'right',
  'middle-start-middle-end': 'left',
  'top-end-bottom-end': 'top-end',
  'top-end-top-start': 'right-start',
  'top-middle-bottom-middle': 'top',
  'top-start-bottom-start': 'top-start',
  'top-start-top-end': 'left-start',
};
type PopoverProps = {
  /** if the popover is open */
  isOpen?: boolean;
  /** The content of the popover */
  children?: React.ReactNode;
  /**
   * Can be used if the anchor should be separated from the trigger.
   * Note: to visually separate the anchor from the trigger, use the Popover.Anchor component instead.
   */
  anchorElement?: Element | null;
  /** If the anchorPosition is provided the anchorElement will be ignored */
  anchorPosition?: {
    x: number;
    y: number;
  };
  /** The placement of the floating element is determined by two sets of words. The first set of words specifies the point on the anchor element where the floating element will be attached. The second set of coordinates specifies the point on the floating element that will attach to the anchor element.*/
  placement?: PlacementOptions;
  /** can take either a number, an object or a function. Full documentation here:  https://floating-ui.com/docs/offset   */
  offset?: OffsetOptions;
  /** Called when an internal state change is triggered, when the floating element either opens or closes. It is not called when `isOpen` is updated by the consumer. */
  onOpenChange?: (isOpen: boolean, event?: Event) => void;
  /** Use anchorEl for portal's container. Should be used if the popover is portaled and is inside a Dialog for example. */
  hasAnchorPortal?: boolean;
  /** If the popover should constrain the focus inside the popover while open */
  shouldCaptureFocus?: boolean;
  /** Which element to initially focus. **/
  initialFocus?: number | React.RefObject<HTMLElement | null>;
  // TODO v5: remove this prop
  /** The role of the popover.
   * @deprecated - This prop will be removed in v5.
   */
  role?:
    | 'tooltip'
    | 'dialog'
    | 'alertdialog'
    | 'menu'
    | 'listbox'
    | 'grid'
    | 'tree';
  /** If the popover should close when the user clicks outside of it */
  closeOnClickOutside?: boolean;
  /** The strategy of the popover. Fixed should be used when the popover is inside a Dialog. */
  strategy?: 'fixed' | 'absolute';
  /** Whether the content is rendered in a portal */
  isPortaled?: boolean;
};

type ContextType = ReturnType<typeof usePopover> | null;

const PopoverContext = React.createContext<ContextType>(null);

export const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);

  if (context === null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }

  return context;
};

const PopoverComponent = ({
  children,
  anchorElement,
  placement,
  isOpen,
  offset,
  anchorPosition,
  hasAnchorPortal: hasAnchorPortalProp,
  shouldCaptureFocus = false,
  initialFocus,
  onOpenChange,
  role,
  closeOnClickOutside = true,
  isPortaled: isPortaledProp,
  strategy: strategyProp,
}: PopoverProps) => {
  const isInsideDialog = useIsInsideDialog();

  const strategy = isInsideDialog ? 'fixed' : 'absolute';
  const hasAnchorPortal = isInsideDialog;
  const isPortaled = !isInsideDialog;

  const popover = usePopover({
    anchorElement: anchorElement,
    anchorElementAsPortalAnchor: hasAnchorPortalProp ?? hasAnchorPortal,
    anchorPosition,
    closeOnClickOutside,
    initialFocus,
    isOpen: isOpen,
    isPortaled: isPortaledProp ?? isPortaled,
    offsetOption: offset,
    onOpenChange: onOpenChange,
    placement: placement ? placementTranslation[placement] : undefined,
    role,
    shouldCaptureFocus: shouldCaptureFocus,
    strategy: strategyProp ?? strategy,
  });

  return (
    <PopoverContext.Provider value={popover}>
      {children}
    </PopoverContext.Provider>
  );
};

type PopoverTriggerProps = {
  /** The content of the trigger */
  children: React.ReactNode;
  /** Whether the trigger should render a button element, or pass its props to the child element */
  hasButtonWrapper?: boolean;
};

const PopoverTrigger = ({
  children,
  hasButtonWrapper = false,
  ref,
  ...props
}: CommonProps<'button', PopoverTriggerProps>) => {
  const context = usePopoverContext();
  // Example from floating UI
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const childrenProps = (children as any).props;
  const mergedRefs = useMergeRefs([
    context.refs.setReference,
    ref,
    childrenProps?.ref,
  ]);

  // `hasButtonWrapper` allows the user to pass any element as the anchor
  if (hasButtonWrapper && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ...props,
        ...childrenProps,
        'data-state': context.isOpen ? 'open' : 'closed',
        ref: mergedRefs,
      }),
    );
  }
  return (
    <button
      ref={context.refs.setReference}
      type="button"
      data-state={context.isOpen ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
};

type PopoverAnchorProps = {
  children?: React.ReactNode;
};

/**
 * Use this to separate the anchor visually from the trigger.
 */
const PopoverAnchor = ({
  children,
  ref,
  ...props
}: CommonProps<'div', PopoverAnchorProps>) => {
  const context = usePopoverContext();

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const childrenProps = (children as any)?.props;
  const mergedRefs = useMergeRefs([
    context.refs.setPositionReference,
    ref,
    childrenProps?.ref,
  ]);

  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...props,
      ...childrenProps,
      ref: mergedRefs,
    });
  }

  return (
    <div ref={mergedRefs} {...props}>
      {children}
    </div>
  );
};

type PopoverContentProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

const PopoverContent = <T extends React.ElementType = 'div'>({
  as,
  className,
  style,
  children,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, PopoverContentProps>) => {
  const { context: floatingContext, ...context } = usePopoverContext();

  const refs = useMergeRefs([context.refs.setFloating, ref]);

  const { themeClassName } = useNeedleTheme();

  const classes = classNames('ndl-popover', themeClassName, className);
  const Component: React.ElementType = as ?? 'div';

  // Patch to not get axe errors for the focus guards
  // To be noted is that axe gives us a false positive error here since the focus guards directly pass focus to other elements
  // https://github.com/floating-ui/floating-ui/issues/2462
  usePatchFloatingFocusGuards();

  if (!floatingContext.open) {
    return null;
  }

  return (
    <ConditionalWrap
      shouldWrap={context.isPortaled}
      wrap={(wrapChildren) => (
        <FloatingPortal
          root={
            (context.anchorElementAsPortalAnchor ?? false)
              ? (context.refs.reference.current as HTMLElement | null)
              : undefined
          }
        >
          {wrapChildren}
        </FloatingPortal>
      )}
    >
      <FloatingFocusManager
        context={floatingContext}
        modal={context.shouldCaptureFocus}
        initialFocus={context.initialFocus}
      >
        <Component
          className={classes}
          aria-labelledby={context.labelId}
          aria-describedby={context.descriptionId}
          style={{
            ...context.floatingStyles,
            ...context.transitionStyles,
            ...style,
          }}
          ref={refs}
          {...context.getFloatingProps({ ...htmlAttributes })}
          {...restProps}
        >
          {children}
        </Component>
      </FloatingFocusManager>
    </ConditionalWrap>
  );
};

const Popover = Object.assign(PopoverComponent, {
  Anchor: PopoverAnchor,
  Content: PopoverContent,
  Trigger: PopoverTrigger,
});

export { Popover };
